/**
 * Iter 141 — My Assignments Inbox.
 *
 * A single pane aggregating everything on the logged-in user's plate:
 * tasks, prospects, meetings, RAID, huddles, approvals, my leaves,
 * bills-to-approve. Data source: GET /me/assignments?ws_id=…
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import {
  ListChecks, Target, CalendarClock, AlertTriangle, Coffee,
  ClipboardCheck, PalmtreeIcon, Receipt, RefreshCw, ArrowRight, Inbox as InboxIcon,
} from "lucide-react";

const CATEGORY_META = {
  tasks: { label: "Tasks", icon: ListChecks, color: "text-primary", link: "/app/tasks" },
  prospects: { label: "Prospects", icon: Target, color: "text-emerald-600", link: "/app/prospecting" },
  meetings: { label: "Meetings", icon: CalendarClock, color: "text-blue-600", link: "/app/meetings" },
  raid: { label: "Log", icon: AlertTriangle, color: "text-amber-600", link: "/app/log" },
  huddles: { label: "Huddles", icon: Coffee, color: "text-purple-600", link: "/app/huddles" },
  approvals: { label: "Approvals", icon: ClipboardCheck, color: "text-rose-600", link: "/app/leaves" },
  my_leaves: { label: "My leaves", icon: PalmtreeIcon, color: "text-teal-600", link: "/app/leaves" },
  bills_to_approve: { label: "Bills to pay", icon: Receipt, color: "text-orange-600", link: "/app/finance/pay-run" },
};

const CATEGORY_ORDER = [
  "tasks", "approvals", "meetings", "huddles", "prospects",
  "raid", "bills_to_approve", "my_leaves",
];

export default function Assignments() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/me/assignments?ws_id=${activeWorkspaceId}&limit_per_category=25`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const visible = useMemo(() => {
    if (!data) return [];
    if (activeCategory === "all") {
      return CATEGORY_ORDER
        .filter((k) => (data.categories?.[k] || []).length > 0)
        .flatMap((k) => (data.categories[k] || []).map((r) => ({ ...r, __category: k })));
    }
    return (data.categories?.[activeCategory] || []).map((r) => ({ ...r, __category: activeCategory }));
  }, [data, activeCategory]);

  return (
    <>
      <PageHeader
        eyebrow="Me"
        title="My assignments"
        description="Everything on your plate right now — across tasks, meetings, approvals, prospects, RAID, huddles and bills. One inbox to zero out every morning."
        testid="assignments-header"
        actions={
          <Button variant="outline" className="rounded-none" onClick={load} disabled={busy} data-testid="assignments-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="px-4 sm:px-6 md:px-10">
        {/* Category count tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
          <CountTile
            testid="assignments-tile-all"
            label="Total"
            count={data?.total ?? 0}
            icon={InboxIcon}
            color="text-foreground"
            active={activeCategory === "all"}
            onClick={() => setActiveCategory("all")}
          />
          {CATEGORY_ORDER.map((key) => {
            const meta = CATEGORY_META[key];
            const count = data?.counts?.[key] ?? 0;
            return (
              <CountTile
                key={key}
                testid={`assignments-tile-${key}`}
                label={meta.label}
                count={count}
                icon={meta.icon}
                color={meta.color}
                active={activeCategory === key}
                onClick={() => setActiveCategory((cur) => cur === key ? "all" : key)}
                disabled={count === 0}
              />
            );
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 md:px-10 pb-10" data-testid="assignments-list">
        {busy && !data ? (
          <div className="text-sm text-muted-foreground p-6">Loading…</div>
        ) : visible.length === 0 ? (
          <EmptyState
            title={data?.total === 0 ? "Inbox zero" : "Nothing here"}
            description={data?.total === 0
              ? "Nothing on your plate right now. Enjoy the quiet."
              : "Pick a different category from above."}
            icon={InboxIcon}
            testid="assignments-empty"
          />
        ) : (
          <div className="border border-border divide-y divide-border">
            {visible.map((row) => <AssignmentRow key={`${row.__category}-${row.id || row.instance_id}`} row={row} />)}
          </div>
        )}
      </div>
    </>
  );
}

function CountTile({ label, count, icon: Icon, color, active, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      className={`border p-3 text-left transition-colors ${
        active ? "border-primary bg-primary/10"
        : disabled ? "border-border/50 opacity-50 cursor-default"
        : "border-border hover:border-foreground/50"
      }`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-1.5 label-eyebrow text-[10px] ${color}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>{count}</div>
    </button>
  );
}

function AssignmentRow({ row }) {
  const category = row.__category;
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  const { title, secondary, badge, link, dateStr } = describeRow(row, category, meta);

  return (
    <Link
      to={link}
      className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 transition-colors"
      data-testid={`assignments-row-${category}-${row.id || row.instance_id}`}
    >
      <Icon className={`w-4 h-4 shrink-0 ${meta.color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        {secondary && <div className="text-xs text-muted-foreground truncate">{secondary}</div>}
      </div>
      {badge && <span className="chip text-[10px] shrink-0">{badge}</span>}
      {dateStr && <span className="text-[11px] text-muted-foreground font-mono shrink-0 w-24 text-right">{dateStr}</span>}
      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </Link>
  );
}

function describeRow(row, category, meta) {
  switch (category) {
    case "tasks":
      return {
        title: row.title || "Untitled task",
        secondary: row.project_name || "",
        badge: row.priority,
        // R93 · `/app/tasks?task=<id>` is the reliable deep-link path;
        // Tasks.jsx auto-opens the drawer when the id is present in the
        // list. The old project-scoped link only worked if a project was
        // linked and the target page listened to the same query param.
        link: `/app/tasks?task=${row.id}`,
        dateStr: row.due_date || "",
      };
    case "prospects":
      return {
        title: row.name || row.email || "Prospect",
        secondary: row.company || row.email || "",
        badge: row.status,
        link: "/app/prospecting",
        dateStr: row.last_activity_at ? fmtRelative(row.last_activity_at) : "",
      };
    case "meetings":
      return {
        title: row.title || "Meeting",
        secondary: row.meeting_type || "",
        badge: null,
        link: `/app/meetings/${row.id}`,
        dateStr: row.scheduled_at ? new Date(row.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "",
      };
    case "raid":
      return {
        title: row.title || "Log item",
        secondary: row.project_name || row.kind || "",
        badge: row.severity || row.kind,
        link: row.project_id ? `/app/projects/${row.project_id}?tab=log&open=${row.id}` : "/app/log",
        dateStr: row.due_date || "",
      };
    case "huddles":
      return {
        title: row.huddle_name || "Huddle",
        secondary: `Status: ${row.my_status || "open"}`,
        badge: "pending",
        link: "/app/huddles",
        dateStr: row.scheduled_at ? new Date(row.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "",
      };
    case "approvals": {
      const isLeave = row.kind === "leave";
      return {
        title: `${isLeave ? "Leave" : "WFH"} · ${row.user_name || "Team member"}`,
        secondary: isLeave ? `${row.from_date} → ${row.to_date}` : row.date,
        badge: row.kind,
        link: "/app/leaves",
        dateStr: row.created_at ? fmtRelative(row.created_at) : "",
      };
    }
    case "my_leaves":
      return {
        title: `${row.from_date} → ${row.to_date}${row.half_day ? " · ½" : ""}`,
        secondary: row.reason || "",
        badge: row.status,
        link: "/app/leaves",
        dateStr: row.status,
      };
    case "bills_to_approve":
      return {
        title: `${row.vendor_name || "Vendor"} · ${row.bill_no || ""}`,
        secondary: `${row.currency} ${(row.outstanding ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} outstanding`,
        badge: row.status,
        link: "/app/finance/pay-run",
        dateStr: row.due_date || "",
      };
    default:
      return { title: "—", secondary: "", badge: null, link: meta.link, dateStr: "" };
  }
}
